
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import AddShop from '../../shops/AddShop';
import { ShopFilter } from '../../shops/components/ShopFilter';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Shop } from '@/lib/supabase/shops/types';

interface ShopsSidebarProps {
  shops: Shop[];
  isCollapsed: boolean;
  setIsCollapsed: (collapsed: boolean) => void;
  isSingleSelectionMode: boolean;
  selectedShops: string[];
  singleSelectedShop: string;
  toggleShopSelection: (id: string) => void;
  setSingleSelectedShop: (id: string) => void;
  filterProductsByShops: () => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  handleAddProduct: () => void;
  isLoading?: boolean;
  refreshShops?: () => Promise<void>;
}

const ShopsSidebar: React.FC<ShopsSidebarProps> = ({
  shops,
  isCollapsed,
  setIsCollapsed,
  isSingleSelectionMode,
  selectedShops,
  singleSelectedShop,
  toggleShopSelection,
  setSingleSelectedShop,
  filterProductsByShops,
  searchTerm,
  setSearchTerm,
  handleAddProduct,
  isLoading = false,
  refreshShops
}) => {
  const [isAddShopDialogOpen, setIsAddShopDialogOpen] = useState(false);
  
  const handleOpenAddShopDialog = () => {
    setIsAddShopDialogOpen(true);
  };
  
  const handleCloseAddShopDialog = () => {
    setIsAddShopDialogOpen(false);
  };
  
  const handleShopAdded = async () => {
    setIsAddShopDialogOpen(false);
    
    // Refresh the shops list
    if (refreshShops) {
      console.log("Rafraîchissement de la liste des boutiques après ajout");
      await refreshShops();
      toast.success("Boutique ajoutée avec succès");
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="shops-sidebar h-full w-64 flex flex-col border-r border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
        <div className="px-6 py-4 flex items-center justify-between flex-shrink-0 border-b border-gray-200 dark:border-gray-800">
          <h2 className="shops-title text-base font-medium text-gray-800 dark:text-gray-200">Boutiques</h2>
        </div>
        <div className="p-4">
          <Skeleton className="h-9 w-full mb-4" />
        </div>
        <div className="px-4 flex-1">
          <Skeleton className="h-10 w-full mb-2" />
          <Skeleton className="h-10 w-full mb-2" />
          <Skeleton className="h-10 w-full mb-2" />
          <Skeleton className="h-10 w-full mb-2" />
          <Skeleton className="h-10 w-full" />
        </div>
      </div>
    );
  }

  // Use the common ShopFilter component
  return (
    <>
      <ShopFilter
        isCollapsed={isCollapsed}
        setIsCollapsed={setIsCollapsed}
        shops={shops}
        isLoading={isLoading}
        toggleShopSelection={toggleShopSelection}
        isSingleSelectionMode={isSingleSelectionMode}
        singleSelectedShop={singleSelectedShop}
        selectedShops={selectedShops}
        showFilterButton={true}
        refreshShops={refreshShops}
        title="Boutiques"
        filterButtonLabel="Appliquer le filtre"
        handleAddShop={handleOpenAddShopDialog}
        // Remove these two lines since ShopFilter manages searchTerm internally
        // searchTerm={searchTerm}
        // setSearchTerm={setSearchTerm}
      />

      {/* Add Shop Dialog */}
      <Dialog open={isAddShopDialogOpen} onOpenChange={setIsAddShopDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto bg-white dark:bg-gray-900">
          <DialogHeader>
            <DialogTitle>Ajouter une boutique</DialogTitle>
          </DialogHeader>
          <AddShop onShopAdded={handleShopAdded} onCancel={handleCloseAddShopDialog} />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ShopsSidebar;
